"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { buttonVariants } from "fumadocs-ui/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "fumadocs-ui/components/ui/popover";
import { useCopyButton } from "fumadocs-ui/utils/use-copy-button";
import { Check, ChevronDown, Copy, ExternalLinkIcon } from "lucide-react";
import { useMemo, useState } from "react";

// import { Logo } from "@/components/logo";
import { cn } from "@/lib/cn";
import { Logo } from "../logo";
const cache = new Map<string, string>();
export function LLMCopyButton({
  /**
   * A URL to fetch the raw Markdown/MDX content of page
   */
  markdownUrl
}: {
  markdownUrl: string;
}) {
  const [isLoading, setLoading] = useState(false);
  const [checked, onClick] = useCopyButton(async () => {
    const cached = cache.get(markdownUrl);
    if (cached) {
      await navigator.clipboard.writeText(cached);
      return;
    }
    setLoading(true);
    try {
      await navigator.clipboard.write([new ClipboardItem({
        "text/plain": fetch(markdownUrl).then(async res => {
          const content = await res.text();
          cache.set(markdownUrl, content);
          return content;
        })
      })]);
    } finally {
      setLoading(false);
    }
  });
  return <button disabled={isLoading} className={cn(buttonVariants({
    color: "secondary",
    size: "sm",
    className: "gap-2 [&_svg]:size-3.5 [&_svg]:text-fd-muted-foreground"
  }))} onClick={onClick}>
			{checked ? <Check /> : <Copy />}
			Copy Markdown
		</button>;
}
const optionVariants = cva("text-sm p-2 rounded-lg inline-flex items-center gap-2 hover:text-fd-accent-foreground hover:bg-fd-accent [&_svg]:size-4");
export function ViewOptions(t0) {
  const $ = _c(25);
  const {
    markdownUrl
  } = t0;
  let t1;
  if ($[0] !== markdownUrl) {
    t1 = typeof window !== "undefined" ? new URL(markdownUrl, window.location.origin) : "loading";
    $[0] = markdownUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fullMarkdownUrl = t1;
  const q = `Read ${fullMarkdownUrl}, I want to ask questions about it.`;
  let t2;
  if ($[2] !== q) {
    t2 = new URLSearchParams({
      hints: "search",
      q
    });
    $[2] = q;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `https://chatgpt.com/?${t2}`;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <svg role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><title>OpenAI</title><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" /></svg>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = {
      title: "Open in ChatGPT",
      href: t3,
      icon: t4
    };
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== q) {
    t6 = new URLSearchParams({
      q
    });
    $[7] = q;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `https://claude.ai/new?${t6}`;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg fill="currentColor" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Anthropic</title><path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" /></svg>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = {
      title: "Open in Claude",
      href: t7,
      icon: t8
    };
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== q) {
    t10 = process.env.NODE_ENV === "development" ? `http://localhost:3003?${new URLSearchParams({
      q
    })}&hints=search&model=auto` : `https://chat.langrouter.ai?${new URLSearchParams({
      q
    })}&hints=search&model=auto`;
    $[12] = q;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Logo />;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10) {
    t12 = {
      title: "Open in LangRouter",
      href: t10,
      icon: t11
    };
    $[15] = t10;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t5 || $[19] !== t9) {
    t13 = [t5, t9, t12];
    $[17] = t12;
    $[18] = t5;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const items = t13;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn(buttonVariants({
      color: "secondary",
      size: "sm",
      className: "gap-2"
    }));
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <PopoverTrigger className={t14}>Open<ChevronDown className="size-3.5 text-fd-muted-foreground" /></PopoverTrigger>;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== items) {
    t16 = <Popover>{t15}<PopoverContent className="flex flex-col overflow-auto">{items.map(_temp)}</PopoverContent></Popover>;
    $[23] = items;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  return t16;
}
function _temp(item) {
  return <a key={item.href} href={item.href} rel="noreferrer noopener" target="_blank" className={cn(optionVariants())}>{item.icon}{item.title}<ExternalLinkIcon className="text-fd-muted-foreground size-3.5 ms-auto" /></a>;
}